import { ReactNode, lazy, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';

import FirebaseTest from '@/components/FirebaseTest';
import Constants from '@/Constants';
import useAccessToken from '@/hooks/useAccessToken';
import useAutoSignout from '@/hooks/useAutoSignout';
import { useButtonDialogBox } from '@/hooks/useButtonDialogBox';
import useCleverTap from '@/hooks/useCleverTap';
import { ElectronFallbackProvider } from '@/hooks/useElectronFallback';
import { InactivityProvider, useInactivityResult } from '@/hooks/useInactivity';
import useServerTime from '@/hooks/useServerTime';
import HomeLayout from '@/layouts/HomeLayout';
import AtHomeScreen from '@/pages/AtHomeScreen';
import CenterValidation from '@/pages/CenterValidation';
import VisibilityTester from '@/pages/VisibilityTester';
import useStore from '@/store';

import useAccessLocation from './hooks/useGetAccessLocation';
import useSendEvents from './hooks/util/useSendEvents';

const StudentLogin = lazy(async () => import('@/pages/StudentLogin'));
const AdminLogin = lazy(async () => import('@/pages/AdminLogin'));
const SubjectSelection = lazy(async () => import('@/pages/SubjectSelection'));
const Instructions = lazy(async () => import('@/pages/Instructions'));
const Test = lazy(async () => import('@/pages/Test'));
const Journey = lazy(async () => import('@/pages/Journey'));
const StudentProfile = lazy(async () => import('@/pages/StudentProfile'));
const AdminProfile = lazy(async () => import('@/pages/AdminProfile'));
const ContentLogs = lazy(async () => import('@/pages/ContentLogs'));
const CourseMode = lazy(async () => import('@/pages/CourseMode'));
const NativeActivityModules = lazy(async () => import('@/pages/NativeActivityModules'));
const NativeSessionPlayer = lazy(async () => import('@/pages/NativeSessionPlayer'));
const CertificateView = lazy(async () => import('@/pages/CertificateView'));

export const ROUTES = {
  STUDENT_LOGIN: '/student-login',
  ADMIN_LOGIN: '/admin-login',
  TASK: '/:index',
  ROOT: '/',
  HOME: '/home',
  INSTRUCTIONS: '/instructions',
  TEST: '/test',
  MISSION_SELECTION: '/mission-selection',
  STAGE_SELECTION: '/stage-selection',
  MODULE_PLAYER: '/module-player',
  JOURNEY: '/journey',
  COURSE_MODE: '/course-mode',
  NATIVE_ACTIVITY_MODULES: '/course-mode/modules',
  NATIVE_SESSION_PLAYER: '/session-player',
  CERTIFICATE_VIEW: '/course-mode/certificate',
  STUDENT_PROFILE: '/student-profile',
  ADMIN_PROFILE: '/admin-profile',
  ADMIN_CONTENT_LOGS: '/admin/content-logs',
  FIREBASE_TEST: '/firebase-test',
  AT_HOME: '/at-home',
  PROFILE: '/profile',
};

function OpenRoutes() {
  const accessToken = useAccessToken();
  return accessToken ? <Navigate to={ROUTES.STUDENT_LOGIN} /> : <Outlet />;
}

function ProtectedRoutes() {
  const accessToken = useAccessToken();
  const { shouldRedirectToRoot } = useAutoSignout();

  if (shouldRedirectToRoot) {
    return <Navigate to={ROUTES.ROOT} replace />;
  }

  return accessToken ? <Outlet /> : <Navigate to={ROUTES.ROOT} />;
}

function StudentInctivityWrapper({ children }: { children: ReactNode }) {
  return (
    <InactivityProvider ms={Constants.AUTO_LOGOUT_TIME} enabled>
      {children}
    </InactivityProvider>
  );
}
function StudentRoutes() {
  const { currentStudent } = useStore();
  const { showButtonDialogBox, closeDialog, open } = useButtonDialogBox();
  const [navigationConfirmed, setNavigationConfirmed] = useState(false);
  const { logoutStudent } = useStore();
  const { inactive } = useInactivityResult();
  const { accessLocation } = useAccessLocation();
  const { studentLogoutEvent } = useSendEvents();
  useServerTime();
  const { t } = useTranslation(['task', 'translation']);
  useCleverTap();

  useEffect(() => {
    if (inactive && currentStudent) {
      logoutStudent({ source: 'inactivity' });
    }
  }, [logoutStudent, inactive, currentStudent]);

  useEffect(() => {
    return () => showButtonDialogBox({ open: false });
  }, [showButtonDialogBox]);

  useEffect(() => {
    if (!currentStudent && !open) {
      studentLogoutEvent('TIMER_LOGOUT');
      showButtonDialogBox({
        open: true,
        title: inactive
          ? t('translation:loggedOutDueToInactivityTitle')
          : t('translation:loggedOut'),
        subtitle: inactive
          ? t('translation:loggedOutDueToInactivity')
          : t('translation:loggedOutDueToLogout'),
        rightButtonText: t('task:okButton'),
        onRightButtonClick: closeDialog,
        onClose: () => {
          setNavigationConfirmed(true);
        },
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        zIndex: 50,
        variant: 'blue',
        showCloseButton: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeDialog, currentStudent, showButtonDialogBox, inactive, t, studentLogoutEvent]);

  return navigationConfirmed ? (
    <Navigate
      to={accessLocation === 'home' ? `${ROUTES.AT_HOME}?step=profile` : ROUTES.STUDENT_LOGIN}
      replace
    />
  ) : (
    <Outlet />
  );
}

function TestRoutes() {
  const { availabilityResponse } = useStore();
  return availabilityResponse ? <Outlet /> : <Navigate to={ROUTES.HOME} />;
}

function SubjectRoutes() {
  const { selectedSubject } = useStore();
  return selectedSubject ? <Outlet /> : <Navigate to={ROUTES.HOME} />;
}

function AdminRoutes() {
  const { currentAdminSession } = useStore();
  return currentAdminSession ? <Outlet /> : <Navigate to={ROUTES.ADMIN_LOGIN} replace />;
}

function ActivityRoutes() {
  const { currentActivity } = useStore();
  return currentActivity ? <Outlet /> : <Navigate to={ROUTES.HOME} />;
}

function SessionRoutes() {
  const { currentSession } = useStore();
  return currentSession ? <Outlet /> : <Navigate to={ROUTES.HOME} />;
}

function AtHomeRoutes() {
  const accessToken = useAccessToken();
  const { accessLocation } = useAccessLocation();
  const { shouldRedirectToRoot } = useAutoSignout();

  if (shouldRedirectToRoot) {
    return <Navigate to={ROUTES.ROOT} replace />;
  }

  const canEnterAtHome = Boolean(accessToken) || accessLocation === 'home';
  return canEnterAtHome ? <Outlet /> : <Navigate to={ROUTES.ROOT} />;
}

function InvalidRouteHandler() {
  const accessToken = localStorage.getItem('accessToken');
  return <Navigate to={accessToken ? ROUTES.STUDENT_LOGIN : ROUTES.ROOT} />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route element={<OpenRoutes />}>
        <Route path={ROUTES.ROOT} element={<CenterValidation />} />
      </Route>
      <Route element={<AtHomeRoutes />}>
        <Route path={ROUTES.AT_HOME} element={<AtHomeScreen />} />
      </Route>
      <Route element={<ProtectedRoutes />}>
        <Route path={ROUTES.STUDENT_LOGIN} element={<StudentLogin />} />
        <Route path={ROUTES.ADMIN_LOGIN} element={<AdminLogin />} />
        <Route path={ROUTES.FIREBASE_TEST} element={<FirebaseTest />} />

        <Route
          element={
            <StudentInctivityWrapper>
              <StudentRoutes />
            </StudentInctivityWrapper>
          }
        >
          <Route path="/visibility-tester" element={<VisibilityTester />} />
          <Route path={ROUTES.HOME} element={<HomeLayout />}>
            <Route element={<SubjectSelection />} index />
          </Route>
          <Route element={<TestRoutes />}>
            <Route path={ROUTES.INSTRUCTIONS} element={<HomeLayout variant="stage" />}>
              <Route element={<Instructions />} index />
            </Route>
            <Route
              path={ROUTES.TEST}
              element={
                <ElectronFallbackProvider>
                  <Test />
                </ElectronFallbackProvider>
              }
            />
          </Route>
          <Route element={<SubjectRoutes />}>
            <Route path={ROUTES.JOURNEY} element={<Journey />} />
          </Route>
          <Route path={ROUTES.COURSE_MODE} element={<CourseMode />} />
          <Route element={<ActivityRoutes />}>
            <Route path={ROUTES.NATIVE_ACTIVITY_MODULES} element={<NativeActivityModules />} />
          </Route>
          <Route element={<SessionRoutes />}>
            <Route
              path={ROUTES.NATIVE_SESSION_PLAYER}
              element={
                <ElectronFallbackProvider>
                  <NativeSessionPlayer />
                </ElectronFallbackProvider>
              }
            />
          </Route>
          <Route path={ROUTES.STUDENT_PROFILE} element={<StudentProfile />} />
          <Route path={ROUTES.CERTIFICATE_VIEW} element={<CertificateView />} />
        </Route>
        <Route element={<AdminRoutes />}>
          <Route path={ROUTES.ADMIN_PROFILE} element={<AdminProfile />} />
          <Route path={ROUTES.ADMIN_CONTENT_LOGS} element={<ContentLogs />} />
        </Route>
      </Route>
      <Route path="*" element={<InvalidRouteHandler />} />
    </Routes>
  );
}
export default AppRoutes;
